/**
 * Petstyle24 – Größen-Sheet (DS layout/SizeSheet; FINAL #5, #31, #40; D+E-Plan §4).
 *
 * Progressive Enhancement: „Kaufen“ auf Produktkarten (context search, compare und duo) ist ein echter Link zur
 * Produktseite. Dieses Skript wertet nur einen normalen Klick bzw. Enter (linke Taste, ohne Strg/Cmd/
 * Umschalt/Alt) zum Größen-Sheet auf. Mittelklick und Strg/Cmd-Klick bleiben Links (neuer Tab).
 * Fehlen Dialog-System, Cart-Adapter oder #PsSizeSheet, bleibt der Link ein Link.
 *
 * Ablauf
 * 1. Inhalt laden: Petstyle24.fetchSection(<produkt-url>, 'size-sheet') – statische Section (Dateiname).
 * 2. Nur [data-ps-size-sheet-content] übernehmen: DOMParser (Petstyle24.parseHTML), importNode,
 *    gleiche Herkunft, kein innerHTML. Ladefehler: Navigation zum href des Links.
 * 3. Öffnen über Petstyle24.dialog.open: Fokus auf X, Esc, Scrim, Fokus-Rückgabe, Touch-Startfokus ohne
 *    Ring (M1.2), Scroll-Lock über dialog[scroll-lock] – kein eigener Dialog, kein eigener Lock.
 * 4. Auswahl: Button-Text und sichtbarer Preis der gewählten Variante. Die Preise sind servergerendert
 *    (ps-money); hier wird nur ein- und ausgeblendet, nie gerechnet. Eine Vorauswahl aus dem Link
 *    (DUO-Karte, ?variant=; sections/size-sheet.liquid) kommt servergerendert; nach dem Einsetzen gleicht
 *    sync() Button und Preis einmal ab (Gesamtplan Et. 10).
 * 5. Hinzufügen: Petstyle24.cart.add(variantId, 1, { open: false }).
 *    Erfolg: Sheet vollständig schließen; erst bei ps:dialog:close für genau dieses Sheet öffnet
 *    Petstyle24.cart.open den Drawer. Zu keinem Zeitpunkt sind zwei Dialoge offen.
 *    Fehler: Meldung im Sheet (role="alert"), nie ein vorgetäuschter Erfolg.
 *
 * window.Petstyle24.sizeSheet.open(link) – für weitere Kaufen-Auslöser (z. B. Quiz-Ergebnis).
 */
(() => {
  'use strict';

  const PS = window.Petstyle24;
  if (!PS || PS.sizeSheet) return;

  const SHEET_ID = 'PsSizeSheet';
  const SECTION = 'size-sheet';
  const ADDED_MS = 2000;
  const strings = (PS.config && PS.config.strings) || {};

  const sheet = () => document.getElementById(SHEET_ID);
  const view = () => {
    const dialog = sheet();
    return dialog ? dialog.querySelector('[data-ps-size-sheet-view]') : null;
  };
  const ready = () => Boolean(PS.dialog && PS.cart && view());
  const anyDialogOpen = () => Boolean(document.querySelector('dialog[open]'));

  let trigger = null;
  let loading = false;
  let pendingHandOff = null;

  const setLabel = (button, value) => {
    const label = button.querySelector('.button__label');
    if (label && value) label.textContent = value;
  };

  const setIcon = (button, visible) => {
    const icon = button.querySelector('.button__icon');
    if (icon) icon.hidden = !visible;
  };

  /* ---------- Laden und Öffnen ---------- */
  const open = async (link) => {
    const url = link ? PS.sameOriginUrl(link.getAttribute('href')) : null;
    if (!url || !ready() || loading || anyDialogOpen()) return false;

    loading = true;
    link.setAttribute('aria-busy', 'true');
    link.classList.add('is-loading');

    let html = null;
    try {
      html = await PS.fetchSection(url.href, SECTION);
    } catch (error) {
      html = null;
    }

    link.removeAttribute('aria-busy');
    link.classList.remove('is-loading');
    loading = false;

    const content = html ? PS.parseHTML(html).querySelector('[data-ps-size-sheet-content]') : null;
    if (!content) {
      // Ladefehler: der Link führt wie ohne JavaScript zur Produktseite
      window.location.assign(url.href);
      return false;
    }
    // DOMParser parst ohne Scripting; <noscript> leeren (Lehre aus KI-25)
    content.querySelectorAll('noscript').forEach((node) => node.replaceChildren());

    if (anyDialogOpen()) return false;
    if (pendingHandOff) {
      pendingHandOff();
      pendingHandOff = null;
    }
    view().replaceChildren(document.importNode(content, true));
    sync();
    trigger = link;
    return PS.dialog.open(sheet(), link);
  };

  /* ---------- Auswahl: Button und sichtbarer Preis ---------- */
  const sync = () => {
    const root = view();
    if (!root) return;
    const checked = root.querySelector('[data-ps-size-selector] input[name="id"]:checked');
    const key = checked ? checked.value : 'none';
    const prices = Array.from(root.querySelectorAll('[data-ps-variant-price]'));
    const match = prices.some((node) => node.dataset.psVariantPrice === key);
    prices.forEach((node) => {
      node.hidden = node.dataset.psVariantPrice !== (match ? key : 'none');
    });
    const submit = root.querySelector('[data-ps-size-sheet-submit]');
    if (checked && submit && !submit.classList.contains('is-loading')) {
      submit.disabled = false;
      setIcon(submit, false);
      setLabel(submit, strings.addToCart);
    }
  };

  const showError = (form, message) => {
    const error = form.querySelector('[data-ps-size-sheet-error]');
    if (error) error.textContent = message || strings.errorGeneric || '';
  };

  const clearError = (form) => {
    const error = form ? form.querySelector('[data-ps-size-sheet-error]') : null;
    if (error) error.textContent = '';
  };

  /* ---------- „Hinzugefügt“ am Kaufen-Link (#40) ---------- */
  const markAdded = (link) => {
    if (!link || !link.isConnected) return;
    const label = link.querySelector('.button__label');
    const before = { text: label ? label.textContent : '', aria: link.getAttribute('aria-label') };
    link.classList.add('is-added');
    setIcon(link, true);
    setLabel(link, strings.added);
    if (link.dataset.psAddedLabel) link.setAttribute('aria-label', link.dataset.psAddedLabel);
    window.setTimeout(() => {
      link.classList.remove('is-added');
      setIcon(link, false);
      if (label) label.textContent = before.text;
      if (before.aria) link.setAttribute('aria-label', before.aria);
    }, ADDED_MS);
  };

  /* ---------- Übergabe an den Drawer: strikt nacheinander ---------- */
  const handOff = (link) => {
    const dialog = sheet();
    const openDrawer = () => {
      // Zu keinem Zeitpunkt zwei modale Dialoge
      if (anyDialogOpen()) return;
      PS.cart.open(link && link.isConnected ? link : undefined);
      markAdded(link);
    };

    if (!dialog || !dialog.open) {
      openDrawer();
      return;
    }

    const unsubscribe = PS.on('dialog:close', (detail) => {
      if (!detail || detail.dialog !== dialog) return;
      unsubscribe();
      pendingHandOff = null;
      openDrawer();
    });
    pendingHandOff = unsubscribe;
    PS.dialog.close(dialog);
  };

  const submit = async (form) => {
    const button = form.querySelector('[data-ps-size-sheet-submit]');
    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
    const field = form.querySelector('input[name="id"]:checked') || form.querySelector('input[type="hidden"][name="id"]');
    if (!field) return;

    const link = trigger;
    clearError(form);
    // Laden: aria-disabled statt disabled, damit der Tastaturfokus auf dem Button bleibt
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    setLabel(button, strings.adding);

    const result = await PS.cart.add(field.value, 1, { open: false });

    button.classList.remove('is-loading');
    button.removeAttribute('aria-busy');
    if (!result || !result.ok) {
      button.removeAttribute('aria-disabled');
      setLabel(button, strings.addToCart);
      showError(form, result && result.error);
      return;
    }
    setIcon(button, true);
    setLabel(button, strings.added);
    handOff(link);
  };

  /* ---------- Ereignisse (Delegation) ---------- */
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest(`#${SHEET_ID} [data-ps-size-sheet-close]`)) {
      event.preventDefault();
      PS.dialog.close(sheet());
      return;
    }

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = target.closest('a[data-ps-size-sheet-open]');
    if (!link || !ready() || !PS.sameOriginUrl(link.getAttribute('href'))) return;
    event.preventDefault();
    open(link);
  });

  document.addEventListener('change', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !target.closest(`#${SHEET_ID} [data-ps-size-selector]`)) return;
    clearError(target.closest('form'));
    sync();
  });

  document.addEventListener('submit', (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form || !form.matches('[data-ps-size-sheet-form]')) return;
    event.preventDefault();
    submit(form);
  });

  PS.sizeSheet = { open };
})();
